import { api } from '@xtnd/web/shared/api'

interface JuniperTestDataRequest {
  operationalBillId: number;
  trrBillId?: number;
  juniperId?: string;
  includePaymentLink?: boolean;
}

interface JuniperDataObject {
  id: string;
  etag: string;
  tenant_id: string;
  created_at: string;
  updated_at: string;
  status: string;
  operational_bill: any;
  trust_request_bill: any;
  matter: any;
  trust: {
    bank_transactions: any[];
  };
  sent_at?: string;
  // Payment link properties
  clio_payment_link?: any;
  clioPaymentLinkId?: string;
  clioPaymentLinkUrl?: string;
  clio_payment_page_qr_code?: string;
}

interface ClioConfigResponse {
  configured: boolean;
  hasAccessToken: boolean;
  hasRefreshToken: boolean;
  clientId: string;
  tokenExpiresAt?: string;
}

interface ClioConfigDetails {
  appId: string;
  clientId: string;
  clientSecret: string | null;
  authorizationCode: string | null;
  baseUrl: string;
  redirectUri: string;
  webhooksConfigured: boolean;
  webhookSubscriptions: any[];
}

interface ClioOAuthUrlResponse {
  authUrl: string;
  state: string;
}

interface ClioOAuthCallbackRequest {
  code: string;
  state: string;
  clientId: string;
  clientSecret: string;
}

interface ClioTestConnectionResponse {
  success: boolean;
  user: { name: string; account?: { name: string } };
  message: string;
}

class ClioIntegrationApi {
  private readonly baseSource = '/tenants/current/clio';

  /**
   * Fetch Juniper data using the same production endpoint
   * Requires operationalBillId, optionally includes trrBillId
   */
  async fetchJuniperTestData(request: JuniperTestDataRequest): Promise<JuniperDataObject> {
    return api.post<JuniperDataObject>(`${this.baseSource}/data?testingCreateLink=true`, request);
  }

  /**
   * Get Clio configuration status
   */
  async getConfigStatus(): Promise<ClioConfigResponse> {
    return api.get<ClioConfigResponse>(`${this.baseSource}/config`);
  }

  /**
   * Get detailed Clio configuration
   */
  async getConfig(): Promise<ClioConfigDetails> {
    return api.get<ClioConfigDetails>(`${this.baseSource}/config`);
  }

  /**
   * Generate OAuth authorization URL
   */
  async generateOAuthUrl(data: {
    clientId: string;
    redirectUri: string;
    clientSecret?: string;
  }): Promise<ClioOAuthUrlResponse> {
    return api.post<ClioOAuthUrlResponse>(`${this.baseSource}/oauth/url`, data);
  }

  /**
   * Exchange OAuth code for tokens
   */
  async exchangeOAuthCode(data: ClioOAuthCallbackRequest): Promise<void> {
    return api.post<void>(`${this.baseSource}/oauth/callback`, data);
  }

  /**
   * Test Clio API connection
   */
  async testConnection(): Promise<ClioTestConnectionResponse> {
    return api.get<ClioTestConnectionResponse>(`${this.baseSource}/test-connection`);
  }

  /**
   * Get Clio bank accounts
   */
  async getBankAccounts(type?: 'trust' | 'operational'): Promise<any[]> {
    const params = type ? { type } : {};
    return api.get<any[]>(`${this.baseSource}/bank-accounts`, { params });
  }
}

export const clioIntegrationApi = new ClioIntegrationApi();

export type {
  JuniperTestDataRequest,
  JuniperDataObject,
  ClioConfigResponse,
  ClioConfigDetails,
  ClioOAuthUrlResponse,
  ClioOAuthCallbackRequest,
  ClioTestConnectionResponse
};
